import React, { useEffect, useState, useRef } from 'react'
import mermaid from 'mermaid'
import { CodeIcon, EyeIcon } from 'lucide-react'

mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Roboto, sans-serif',
})

interface MermaidProps {
  chart: string
}

export function Mermaid({ chart }: MermaidProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<boolean>(false)
  const [view, setView] = useState<'preview' | 'code'>('preview')

  useEffect(() => {
    if (view === 'preview' && containerRef.current && chart) {
      const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`
      setError(false)
      containerRef.current.innerHTML = ''
      mermaid
        .render(id, chart)
        .then((result) => {
          if (containerRef.current) {
            containerRef.current.innerHTML = result.svg
          }
        })
        .catch((e) => {
          console.error('Mermaid rendering failed', e)
          setError(true)
        })
    }
  }, [chart, view])

  return (
    <div className="my-8 rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm not-prose">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <span className="text-sm font-bold text-gray-700 font-['Poppins']">Diagram</span>
        <div className="flex space-x-2 bg-gray-200/50 p-1 rounded-lg">
          <button
            onClick={() => setView('preview')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center transition-all duration-200 ${
              view === 'preview' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <EyeIcon className="w-4 h-4 mr-1.5" />
            Preview
          </button>
          <button
            onClick={() => setView('code')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center transition-all duration-200 ${
              view === 'code' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <CodeIcon className="w-4 h-4 mr-1.5" />
            Code
          </button>
        </div>
      </div>
      <div className="p-4">
        {view === 'preview' ? (
          error ? (
            <div className="text-red-500 text-sm font-['Roboto']">Failed to render diagram. Check the syntax.</div>
          ) : (
            <div ref={containerRef} className="flex justify-center" />
          )
        ) : (
          <pre className="text-sm text-gray-800 overflow-x-auto font-mono whitespace-pre-wrap">{chart}</pre>
        )}
      </div>
    </div>
  )
}